import React, { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Spin, Tag } from 'antd';
import {
	DownloadOutlined,
	FilePdfOutlined,
	SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import BannerWithImage from 'components/BannerWithImage';
import { API } from 'utils';
import styles from './VerificarReferencia.module.less';

interface ReferenciaVerificada {
	tipoDocumento: string | null;
	nombreAfiliado: string | null;
	tipoReferencia: string | null;
	numeroReferencia: number | null;
	nombreMedicoEmisor: string | null;
	juntaVigilanciaEmisor: string | null;
	fechaEmision: FechaApi;
	nombreMedicoAutorizante: string | null;
	juntaVigilanciaAutorizante: string | null;
	estado: string | null;
	fechaAutorizacion: FechaApi;
}

type FechaApi = string | number[] | null;

const mostrarValor = (value: string | number | null) => {
	if (value === null || value === '') return 'No disponible';
	return value;
};

const convertirFecha = (value: FechaApi) => {
	if (!value) return 'No disponible';

	if (Array.isArray(value)) {
		const [year, month, day, hour = 0, minute = 0, second = 0] = value;
		return new Date(year, month - 1, day, hour, minute, second);
	}

	const isoLocal = value.match(
		/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/
	);
	if (isoLocal) {
		return new Date(
			Number(isoLocal[1]),
			Number(isoLocal[2]) - 1,
			Number(isoLocal[3]),
			Number(isoLocal[4] ?? 0),
			Number(isoLocal[5] ?? 0),
			Number(isoLocal[6] ?? 0)
		);
	}

	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? value : date;
};

const formatearFecha = (value: FechaApi) => {
	const date = convertirFecha(value);
	if (typeof date === 'string') return date;

	return new Intl.DateTimeFormat('es-SV', {
		year: 'numeric',
		month: 'long',
		day: '2-digit',
	}).format(date);
};

const formatearFechaHora = (value: FechaApi) => {
	const date = convertirFecha(value);
	if (typeof date === 'string') return date;

	return new Intl.DateTimeFormat('es-SV', {
		year: 'numeric',
		month: 'long',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	}).format(date);
};

const colorEstado = (estado: string | null) => {
	switch (estado) {
		case 'VALIDADO':
			return 'green';
		case 'PENDIENTE':
			return 'gold';
		case 'OBSERVADO':
			return 'orange';
		case 'DENEGADO':
		case 'ANULADA':
			return 'red';
		default:
			return 'default';
	}
};

const VerificarReferencia = () => {
	const { token } = useParams<Record<string, string | undefined>>();
	const [referencia, setReferencia] = useState<ReferenciaVerificada | null>(
		null
	);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [pdfLoading, setPdfLoading] = useState(false);
	const [pdfError, setPdfError] = useState<string | null>(null);

	useEffect(() => {
		if (!token) {
			setError('El enlace de verificación no es válido.');
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(null);
		setReferencia(null);

		const consultarReferencia = async () => {
			try {
				const response = await API.getVerificacionReferencia(token);
				if (response.status === 200 && response.data) {
					setReferencia(response.data as ReferenciaVerificada);
					return;
				}

				if (response.status === 404) {
					setError(
						'La referencia no existe o el código de verificación fue alterado.'
					);
					return;
				}

				setError('No fue posible verificar la referencia en este momento.');
			} catch (requestError) {
				console.error('Error verificando referencia:', requestError);
				setError('No fue posible verificar la referencia en este momento.');
			} finally {
				setLoading(false);
			}
		};

		consultarReferencia();
	}, [token]);

	useEffect(() => {
		if (!token || !referencia) return;

		let active = true;
		let objectUrl: string | null = null;

		setPdfLoading(true);
		setPdfError(null);
		setPdfUrl(null);

		const generarPdf = async () => {
			try {
				const response = await API.getReportePdf('2', token);
				if (response.status !== 200 || !response.data) {
					throw new Error('El servicio no generó un PDF válido.');
				}

				const blob = response.data as Blob;
				if (blob.size === 0) {
					throw new Error('El PDF generado está vacío.');
				}

				objectUrl = URL.createObjectURL(blob);
				if (!active) {
					URL.revokeObjectURL(objectUrl);
					objectUrl = null;
					return;
				}

				setPdfUrl(objectUrl);
			} catch (requestError) {
				console.error('Error generando PDF de referencia:', requestError);
				if (active) {
					setPdfError(
						'Los datos fueron verificados, pero no fue posible generar el PDF en este momento.'
					);
				}
			} finally {
				if (active) setPdfLoading(false);
			}
		};

		generarPdf();

		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [referencia, token]);

	const descargarPdf = () => {
		if (!pdfUrl) return;

		const numero = referencia?.numeroReferencia ?? 'documento';
		const link = document.createElement('a');
		link.href = pdfUrl;
		link.download = `referencia-${numero}.pdf`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const abrirPdf = () => {
		if (!pdfUrl) return;
		window.open(pdfUrl, '_blank', 'noopener,noreferrer');
	};

	return (
		<section className={styles.pageWrapper}>
			<BannerWithImage>
				<div className={styles.contentWrapper}>
					<div className={styles.pageHeader}>
						<h4>
							<SafetyCertificateOutlined /> Verificación de referencia
						</h4>
						<p>Consulta de autenticidad y autorización del documento.</p>
					</div>

					<div className={styles.card}>
						{loading && (
							<div className={styles.loadingWrapper}>
								<Spin size='large' />
								<p>Verificando referencia...</p>
							</div>
						)}

						{error && !loading && (
							<Alert
								type='error'
								showIcon
								message='Documento no verificado'
								description={error}
							/>
						)}

						{referencia && !loading && (
							<>
								<Alert
									className={styles.verifiedAlert}
									type='success'
									showIcon
									message='Documento verificado'
									description='La información fue consultada directamente en los registros del ISBM.'
								/>

								<Descriptions
									bordered
									column={{ xs: 1, sm: 1, md: 2 }}
									size='middle'
								>
									<Descriptions.Item label='Tipo de documento'>
										{mostrarValor(referencia.tipoDocumento)}
									</Descriptions.Item>
									<Descriptions.Item label='Estado'>
										<Tag color={colorEstado(referencia.estado)}>
											{mostrarValor(referencia.estado)}
										</Tag>
									</Descriptions.Item>
									<Descriptions.Item label='Nombre del afiliado' span={2}>
										{mostrarValor(referencia.nombreAfiliado)}
									</Descriptions.Item>
									<Descriptions.Item label='Tipo de referencia' span={2}>
										{mostrarValor(referencia.tipoReferencia)}
									</Descriptions.Item>
									<Descriptions.Item label='Número de referencia'>
										{mostrarValor(referencia.numeroReferencia)}
									</Descriptions.Item>
									<Descriptions.Item label='Fecha de emisión'>
										{formatearFecha(referencia.fechaEmision)}
									</Descriptions.Item>
									<Descriptions.Item
										label='Médico que emitió la referencia'
										span={2}
									>
										{mostrarValor(referencia.nombreMedicoEmisor)}
									</Descriptions.Item>
									<Descriptions.Item
										label='Junta de vigilancia del emisor'
										span={2}
									>
										{mostrarValor(referencia.juntaVigilanciaEmisor)}
									</Descriptions.Item>
									<Descriptions.Item
										label='Médico que autoriza la referencia'
										span={2}
									>
										{mostrarValor(referencia.nombreMedicoAutorizante)}
									</Descriptions.Item>
									<Descriptions.Item
										label='Junta de vigilancia del autorizante'
										span={2}
									>
										{mostrarValor(referencia.juntaVigilanciaAutorizante)}
									</Descriptions.Item>
									<Descriptions.Item
										label='Fecha y hora de autorización'
										span={2}
									>
										{formatearFechaHora(referencia.fechaAutorizacion)}
									</Descriptions.Item>
								</Descriptions>
							</>
						)}
					</div>

					{referencia && !loading && (
						<div className={`${styles.card} ${styles.pdfCard}`}>
							<div className={styles.pdfHeader}>
								<div>
									<h5>
										<FilePdfOutlined /> Documento PDF
									</h5>
									<p>Referencia emitida por el ISBM.</p>
								</div>

								{pdfUrl && !pdfLoading && (
									<div className={styles.pdfActions}>
										<Button icon={<FilePdfOutlined />} onClick={abrirPdf}>
											Abrir PDF
										</Button>
										<Button
											type='primary'
											icon={<DownloadOutlined />}
											onClick={descargarPdf}
										>
											Descargar PDF
										</Button>
									</div>
								)}
							</div>

							{pdfLoading && (
								<div className={styles.pdfLoadingWrapper}>
									<Spin size='large' />
									<p>Generando documento PDF...</p>
								</div>
							)}

							{pdfError && !pdfLoading && (
								<Alert
									type='warning'
									showIcon
									message='PDF no disponible'
									description={pdfError}
								/>
							)}

							{pdfUrl && !pdfLoading && (
								<div className={styles.pdfViewer}>
									<object
										aria-label='Documento PDF de la referencia'
										data={pdfUrl}
										type='application/pdf'
										width='100%'
										height='100%'
									>
										<p>
											El navegador no puede mostrar el PDF. Utilice el botón
											Descargar PDF.
										</p>
									</object>
								</div>
							)}
						</div>
					)}
				</div>
			</BannerWithImage>
		</section>
	);
};

export default VerificarReferencia;
